<script lang="ts">
  import type { GatFormFieldProps } from '../gat-form-field'
  import GatPhoneEditFunctions from './functions/GatPhoneEditFunctions'
  import {
    ComboboxAnchor,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxGroup,
    ComboboxInput,
    ComboboxItem,
    ComboboxItemIndicator,
    ComboboxLabel,
    ComboboxRoot,
    ComboboxSeparator,
    ComboboxTrigger,
    ComboboxViewport,
    useFilter,
    type ListboxItemSelectEvent
  } from 'reka-ui'
  import { parsePhoneNumber } from 'awesome-phonenumber'

  export type GatPhoneEditModelValue = string | null | undefined

  export interface GatPhoneEditProps
    extends GatFormFieldProps<GatPhoneEditModelValue> {
    modelValue: GatPhoneEditModelValue
    /** Input placeholder if the input is empty */
    placeholder?: string
    /** If the country the phonenumber belongs to is known. If not set the userCountryCode will be used as country */
    defaultRegionCode?: RegionCode | (string & {})
    /** If only mobile (can receive SMS) number should be allowed. Warning will be shown if number is not a mobile number */
    mobileOnly?: boolean
    /** If the input should be focused when the component is mounted */
    autofocus?: boolean
  }

  export type GatPhoneEditEmits = {
    (e: 'update:modelValue', value: GatPhoneEditModelValue): void
    (e: 'input', value: string): void
  }
</script>
<script setup lang="ts">
  import { icons } from '@gat-ship/icons'
  import {
    computed,
    nextTick,
    onMounted,
    ref,
    useTemplateRef,
    watch,
    type ComponentPublicInstance
  } from 'vue'
  import { cn } from '../../lib/utils'
  import GatFlag from '../gat-flag/gat-flag.vue'
  import GatIcon from '../gat-icon/gat-icon.vue'
  import {
    Command,
    CommandList,
    CommandInput,
    CommandEmpty,
    CommandGroup,
    CommandItem
  } from '../ui/command'
  import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover'
  import { ScrollArea } from '../ui/scroll-area'
  import { phoneCodeMapping } from './all-countries'
  import type { RegionCode } from './all-countries'
  import { useCountryCode } from './composables/useCountryCode'
  import { useCountryDialNumber } from './composables/useCountryDialNumber'
  import { useGatPhoneEditValidation } from './composables/useGatPhoneEditValidation'
  import type { CountryAndDialCode } from './types/CountryAndDialCode'
  import { GatFormField } from '../gat-form-field'
  import RegionCodeTag from './region-code-tag.vue'

  /** This component is heavily inspired from https://shadcn-phone-input.vercel.app/ */

  const props = withDefaults(defineProps<GatPhoneEditProps>(), {
    showOptional: undefined,
    validateOn: 'blur',
    hideDetails: 'auto',
    defaultValue: undefined,
    defaultRegionCode: undefined
  })

  const emit = defineEmits<GatPhoneEditEmits>()

  const { countryCode } = useCountryCode(props)

  const { countryDialNumber } = useCountryDialNumber(countryCode)

  const { phoneEditRules } = useGatPhoneEditValidation(
    props,
    props.rules,
    countryDialNumber,
    countryCode
  )

  const field = useTemplateRef('field')

  const formField = useTemplateRef('form-field')

  async function focus() {
    await nextTick()
    const inputEl = field.value
    if (inputEl) {
      inputEl.focus?.()
    }
  }

  onMounted(() => {
    if (props.autofocus) {
      focus()
    }
  })

  const selectedRegionCode = ref<RegionCode | (string & {}) | undefined>(
    undefined
  )

  watch(selectedRegionCode, (newVal) => {
    if (newVal) {
      const value = inputValue.value || ''
      const parsed = parsePhoneNumber(value, {
        regionCode: newVal
      })
      if (parsed.valid && parsed.number) {
        emit('update:modelValue', parsed.number.e164)
      } else {
        emit('update:modelValue', undefined)
      }
    }
  })

  watch(
    () => props.defaultRegionCode,
    (newVal) => {
      if (newVal) {
        selectedRegionCode.value = newVal.toLowerCase()
      }
    },
    {
      immediate: true
    }
  )

  const inputValue = ref<string | undefined>(undefined)

  watch(inputValue, (newVal) => {
    if (newVal) {
      const parsed = parsePhoneNumber(newVal, {
        regionCode: selectedRegionCode.value
      })
      if (parsed.valid) {
        emit('update:modelValue', parsed.number.e164)
      }
    } else {
      emit('update:modelValue', undefined)
    }
  })

  watch(
    () => props.modelValue,
    (newVal) => {
      if (newVal) {
        const parsed = parsePhoneNumber(newVal)
        if (parsed.valid) {
          inputValue.value = parsed.number.national
          selectedRegionCode.value = parsed.regionCode.toLowerCase()
        } else {
          inputValue.value = newVal
        }
      } else {
        inputValue.value = undefined
        selectedRegionCode.value = undefined
      }
    },
    {
      immediate: true
    }
  )

  const displayValue = computed(() => {
    const value = inputValue.value
    if (!value) {
      return undefined
    }
    const parsed = parsePhoneNumber(value, {
      regionCode: selectedRegionCode.value
    })
    if (parsed.valid) {
      return parsed.number.national
    }
    return value
  })

  const regionCodeDropdownIsOpen = ref(false)

  watch(regionCodeDropdownIsOpen, (isOpen) => {
    if (!isOpen) {
      comboboxSearchTerm.value = ''
      searchTerm.value = ''
    }
  })

  defineExpose({
    focus
  })

  const searchTerm = ref('')

  const comboboxSearchTerm = ref('')

  function handleSelect(event: ListboxItemSelectEvent<string>) {
    const searchTermValue = searchTerm.value || comboboxSearchTerm.value
    selectedRegionCode.value = event.detail.value
    regionCodeDropdownIsOpen.value = false
    if (inputValue.value?.trim() === searchTermValue.trim()) {
      inputValue.value = ''
    }
  }

  function handleInput(event: Event) {
    const value = (event.target as HTMLInputElement).value
    console.log('value', value)
    let dialCodeSet = false
    if (!selectedRegionCode.value && value.includes(' ')) {
      const [inputDialCode, phoneNumber] = value.split(' ')
      const countryAndDialCode = phoneCodeMapping.find(
        ([_, regionCode, dialCode]) =>
          dialCode === inputDialCode.replace('+', '')
      )
      if (countryAndDialCode) {
        selectedRegionCode.value = countryAndDialCode[1]
        inputValue.value = phoneNumber
        regionCodeDropdownIsOpen.value = false
        dialCodeSet = true
      }
    }
    if (!dialCodeSet) {
      inputValue.value = value
    }
    if (value === '+') {
      regionCodeDropdownIsOpen.value = true
    }
    if (!selectedRegionCode.value && value.startsWith('+')) {
      searchTerm.value = value
    }
    if (filteredPhoneCodeMapping.value.length === 0) {
      regionCodeDropdownIsOpen.value = false
    }
  }

  function handleBlur(event: FocusEvent) {
    formField.value?.handleBlur(event)
  }

  function handleFocus() {
    if (selectedRegionCode.value) {
      return
    }
  }

  function focusOnComboboxSearchInput() {
    nextTick(() => {
      comboboxSearchInputRef.value?.focus()
    })
  }

  function handleKeyDown(event: KeyboardEvent) {
    const caretStartPosition = (event.target as HTMLInputElement).selectionStart
    const caretEndPosition = (event.target as HTMLInputElement).selectionEnd
    if (event.key === 'Backspace') {
      if (
        caretStartPosition === 0 &&
        caretStartPosition === caretEndPosition &&
        selectedRegionCode.value
      ) {
        selectedRegionCode.value = undefined
      }
    }
    if (event.key === 'Enter') {
      event.preventDefault()
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      regionCodeDropdownIsOpen.value = false
    }
    if (event.key === 'ArrowDown' || (event.key === ' ' && event.ctrlKey)) {
      event.preventDefault()
      if (!regionCodeDropdownIsOpen.value) {
        regionCodeDropdownIsOpen.value = true
        return
      }
      focusOnComboboxSearchInput()
    }
    if (event.key === 'Tab') {
      if (regionCodeDropdownIsOpen.value) {
        event.preventDefault()
        focusOnComboboxSearchInput()
        return
      }
    }
  }

  const { contains } = useFilter({ sensitivity: 'base' })

  const filteredPhoneCodeMapping = computed(() => {
    if (comboboxSearchTerm.value) {
      return phoneCodeMapping.filter(([countryName, regionCode, dialCode]) =>
        contains(`+${dialCode} ${countryName}`, comboboxSearchTerm.value)
      )
    }
    return phoneCodeMapping.filter(([_, regionCode, dialCode]) =>
      contains(`+${dialCode}`, searchTerm.value)
    )
  })

  const comboboxSearchInputRef = ref<InstanceType<
    typeof HTMLInputElement
  > | null>(null)

  function onRegionCodeTagClick() {
    regionCodeDropdownIsOpen.value = true
    focusOnComboboxSearchInput()
  }

  function selectFirstItem() {
    if (filteredPhoneCodeMapping.value.length > 0) {
      selectedRegionCode.value = filteredPhoneCodeMapping.value[0][1]
    }
  }

  function handlePaste(event: ClipboardEvent) {
    const value = event.clipboardData?.getData('text')
    if (value) {
      const parsed = parsePhoneNumber(value)
      if (parsed.valid) {
        selectedRegionCode.value = parsed.regionCode.toLowerCase()
        inputValue.value = parsed.number.national
      } else {
        inputValue.value = value
      }
    }
  }

  const comboboxContentRef = ref<InstanceType<typeof ComboboxContent> | null>(
    null
  )

  const comboboxItemRefs = ref<ComponentPublicInstance[]>([])

  function setItemRef(el: ComponentPublicInstance | null, index: number) {
    if (el) comboboxItemRefs.value[index] = el
  }

  function handleComboboxSearchInputKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      selectFirstItem()
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      // programatically target the next item
      const firstComboboxItem = comboboxItemRefs.value[0]?.$el
      if ('focus' in firstComboboxItem) {
        firstComboboxItem.focus()
      }
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      field.value?.focus()
    }
  }

  function handleComboboxItemKeyDown(event: KeyboardEvent, index: number) {
    if (event.key === 'ArrowUp' && index === 0) {
      focusOnComboboxSearchInput()
    }
  }
</script>
<template>
  <gat-form-field
    ref="form-field"
    v-bind="props"
    class="overflow-visible"
    :rules="phoneEditRules"
    :value="modelValue"
    @value-updated="emit('update:modelValue', $event)"
  >
    <template
      #default="{ isValid, name, disabled, inputClass, as: asComponent }"
    >
      <combobox-root
        class="relative"
        :open="regionCodeDropdownIsOpen"
        :ignore-filter="true"
        as-child
      >
        <combobox-anchor class="w-full">
          <div class="flex w-full flex-col items-start space-y-1">
            <div class="flex w-full">
              <region-code-tag
                v-if="selectedRegionCode"
                class="mt-1 shrink-0"
                :regionCode="selectedRegionCode"
                :countryCode="countryCode"
                @click="onRegionCodeTagClick"
              />

              <input
                :id="name"
                ref="field"
                autocomplete="off"
                :value="displayValue"
                :data-testid="dataTestid"
                :placeholder
                :disabled="disabled"
                :class="
                  cn(
                    inputClass,
                    'w-full grow rounded-e-lg rounded-s-none',
                    props.class
                  )
                "
                @input="handleInput"
                @focus="handleFocus"
                @blur="handleBlur"
                @keydown="handleKeyDown"
                @paste.prevent="handlePaste"
              />
            </div>
          </div>
          <combobox-content
            class="data-[side=top]:animate-slideDownAndFade data-[side=right]:animate-slideLeftAndFade data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade absolute z-40 mt-1 flex w-full min-w-[160px] flex-col rounded border bg-white shadow-sm will-change-[opacity,transform]"
            @focus-outside.prevent
            @interact-outside.prevent
            @pointer-down-outside.prevent="regionCodeDropdownIsOpen = false"
            ref="comboboxContentRef"
          >
            <div class="flex w-full flex-col p-1">
              <div class="mb-1 flex w-full items-center gap-1 rounded border">
                <gat-icon
                  icon="Search"
                  class="text-soft absolute left-2 size-3"
                />
                <input
                  class="w-full py-1 pl-5 text-sm font-semibold"
                  v-model="comboboxSearchTerm"
                  ref="comboboxSearchInputRef"
                  @keydown.stop="handleComboboxSearchInputKeyDown"
                />
              </div>
              <div class="max-h-[200px] w-full overflow-auto">
                <combobox-item
                  v-for="(
                    [countryName, regionCode, dialCode], index
                  ) in filteredPhoneCodeMapping"
                  :ref="
                    (el) => setItemRef(el as ComponentPublicInstance, index)
                  "
                  :key="regionCode"
                  :value="regionCode"
                  class="hover:bg-accent cursor-pointer"
                  @select.prevent="handleSelect($event)"
                  @keydown="handleComboboxItemKeyDown($event, index)"
                >
                  <div class="flex w-fit items-center gap-2 px-2 py-1">
                    <gat-flag
                      :flag="regionCode.toUpperCase()"
                      class="size-4 rounded-full"
                    />
                    <span class="whitespace-nowrap text-sm">{{
                      countryName
                    }}</span>
                    <span class="text-soft text-sm">(+{{ dialCode }})</span>
                  </div>
                </combobox-item>
              </div>
            </div>
          </combobox-content>
        </combobox-anchor>
      </combobox-root>
    </template>
    <template #messages-long="{ messages }">
      <slot name="messages-long" v-bind="{ messages }" />
    </template>
  </gat-form-field>
</template>
